import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { Promotion } from "admin/types/promotion";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { Meta } from "admin/types/meta";
import { PromotionApplicableConditionTypes } from "../../constants/promotionApplicableConditionTypes";

const key = "promotion";
const adapter = createEntityAdapter<Promotion.Promotion>();

const initialStateValues: Promotion.PromotionState = {
  errors: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchPromotions = createAsyncThunk<
  {
    promotions: Promotion.Promotion[];
    meta: Meta;
  },
  {
    context: PageContext.PageContext;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchPromotions`, async (args, { rejectWithValue }) => {
  try {
    const { context, param, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/promotions.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/promotions.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      promotions: result.data.promotions,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchPromotion = createAsyncThunk<
  {
    promotion: Promotion.Promotion;
  },
  {
    context: PageContext.PageContext;
    id: number;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchPromotion`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/promotions/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/promotions/${id}.json`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      promotion: result.data.promotion,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postPromotion = createAsyncThunk<
  { promotion: Promotion.Promotion },
  { context: PageContext.PageContext; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postPromotion`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/promotions.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/promotions.json`;
    const result = await createWrapper().post(url, { promotion: params });
    return { promotion: result.data.promotion };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putPromotion = createAsyncThunk<
  { promotion: Promotion.Promotion },
  {
    context: PageContext.PageContext;
    id: number;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putPromotion`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/promotions/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/promotions/${id}.json`;
    const result = await createWrapper().put(url, { promotion: params });
    return { promotion: result.data.promotion };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const promotionSlice = createSlice({
  name: key,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.promotion };
      })
      .addCase(fetchPromotions.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.promotions);
        state.meta = action.payload.meta;
      })
      .addCase(fetchPromotion.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.promotion);
      })
      .addCase(postPromotion.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.promotion);
      })
      .addCase(putPromotion.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.promotion);
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: promotionByIdSelector,
  selectIds: promotionIdsSelector,
  selectEntities: promotionEntitiesSelector,
  selectAll: promotionsSelector,
  selectTotal: totalPromotionsSelector,
} = adapter.getSelectors((state: RootState) => state.promotion);

const stateSelector = (state: RootState) => state[key];

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const promotionsMetaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

export const convertPromotionToFormValues = (data: any) => {
  const values = { ...data };
  if (values.hotels) {
    values.hotel_codes = values.hotels.map((hotel) => hotel.code);
    delete values.hotels;
  }
  return values;
};

// for Server side
export const redirectPropsIfRoomPlanPromotion = (
  promotionResult: {
    promotion?: Promotion.Promotion;
  },
  context: PageContext.PageContext,
) => {
  const promotion = promotionResult?.promotion;
  if (
    !promotion ||
    (promotion.applicableCondition !==
      PromotionApplicableConditionTypes.hotel &&
      promotion.hotels?.length > 0)
  ) {
    const hotelCode = promotion.hotels[0].code;
    const destination =
      context.adminType === AdminTypes.manager
        ? `/manager/companies/${context.companyCode}/hotels/${hotelCode}/promotions/${promotion.id}`
        : `/admin/hotels/${hotelCode}/promotions/${promotion.id}`;
    return {
      redirect: {
        destination: destination,
        permanent: false,
      },
    };
  }
};

export const redirectPathIfRoomPlanPromotion = (
  promotionResult: {
    promotion?: Promotion.Promotion;
  },
  context: PageContext.PageContext,
) => {
  const promotion = promotionResult?.promotion;
  if (
    !promotion ||
    (promotion.applicableCondition !==
      PromotionApplicableConditionTypes.hotel &&
      promotion.hotels?.length > 0)
  ) {
    const hotelCode = promotion.hotels[0].code;
    return context.adminType === AdminTypes.manager
      ? `/manager/companies/${context.companyCode}/hotels/${hotelCode}/promotions/${promotion.id}`
      : `/admin/hotels/${hotelCode}/promotions/${promotion.id}`;
  }
};
